import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { isObject, isUndefinedOrNull } from 'src/utils/general';
import { useDocumentStore } from 'src/store/main';
import displayFieldLabel from 'src/utils/displayFieldLabel';
import docTablesUtil from 'src/utils/docTablesUtil';

export const usePOLineItemsStore = defineStore('po_line_items', {
  state: () => ({
    doc_store: useDocumentStore(),
    tableColumns: [] as any,
    formatting_options: {} as dynamicObj,
    custom_columns_to_hide: [] as string[],
    columns_to_hide: [] as string[],
    lineItemLayout: {} as dynamicObj,
    dropdownColumns: [] as dynamicObj[],
    layoutExtraColumns: [] as string[],
  }),
  getters: {},
  actions: {
    prepareTableLayoutObj() {
      // remove duplicate
      this.dropdownColumns = [];
      const duplicate_columns = {} as dynamicObj;
      this.lineItemLayout = {} as dynamicObj;

      if (
        !(
          this.doc_store.record.tables &&
          this.doc_store.record.tables.length > 0 &&
          this.doc_store.record.tables[0].hasOwnProperty('layout')
        )
      ) {
        return;
      }

      Array.from(this.doc_store.record.tables[0].layout).forEach(
        (column: any) => {
          if (
            !duplicate_columns.hasOwnProperty(column.column_name) &&
            column.is_hidden == false &&
            !this.isCustomColumnHidden(column.column_name)
          ) {
            duplicate_columns[column.column_name] = true;

            // prepare Coloum layout
            column['lbl_to_display'] =
              displayFieldLabel.methods.getLabelToDisplay(column);
            this.lineItemLayout[column.column_name] = column;

            // prepare Coloum Dropdown
            this.dropdownColumns.push({
              header: column.lbl_to_display,
              techName: column.column_name,
            });
          }
        },
      );
    },
    // Table Columns for PO Match And Calculation
    getTableColumns: function () {
      this.tableColumns = [] as dynamicObj[];
      if (
        !this.doc_store.record.hasOwnProperty('tables') ||
        !this.doc_store.record.tables ||
        !this.doc_store.record.tables.length ||
        !this.doc_store.record.tables[0].hasOwnProperty('rows') ||
        !this.doc_store.record.tables[0].rows.length
      ) {
        return;
      }
      if (this.doc_store.has_ai_table) {
        this.getTableColumnsForAI();
        return;
      }
      this.normalTableColumns();
    },

    getTableColumnsForAI() {
      const added_columns = {} as dynamicObj;
      const tempColumnArray: any = [];

      Array.from(this.doc_store.record.tables[0].rows).forEach((row: any) => {
        Array.from(row.columns).forEach((column: any) => {
          const allowed = this.is_AI_isColumnHidden(row, column);
          if (allowed && !added_columns.hasOwnProperty(column.name)) {
            added_columns[column.name] = true;
            let label = column.name;
            let dataType = '';
            let isRequired = '';
            let alwaysVisible = '';

            if (this.lineItemLayout.hasOwnProperty(column.name)) {
              label = this.lineItemLayout[column.name].lbl_to_display;
              dataType = this.lineItemLayout[column.name].column_type;
              isRequired = this.lineItemLayout[column.name].is_required;
              alwaysVisible = this.lineItemLayout[column.name].always_visible;
            } else {
              //logInfo(row, column)
            }
            let org_index = '';
            if (column.org_index != null && column.org_index != undefined) {
              org_index = String(column.org_index);
            }

            const columnItem = {
              name: column.name,
              field: column.name,
              align: 'left',
              label: label,
              is_mapped: column.is_mapped ? column.is_mapped : false,
              org_index: org_index !== '' ? org_index : '',
              dataType: dataType ? dataType.toLowerCase() : 'string',
              sortable: true,
              is_required: isRequired,
              always_visible: alwaysVisible,
              is_extra_column: column.hasOwnProperty('is_extra_column') ? column.is_extra_column : false,
              original_name: this.getOrignalColumnName(column),
            };

            const is_non_mapped_allowed =
              columnItem.is_mapped ||
              (!this.doc_store.remove_unmapped_columns &&
                !columnItem.is_mapped);

            if (
              column.hasOwnProperty('org_index') &&
              column.org_index != null &&
              is_non_mapped_allowed
            ) {
              this.tableColumns.push(columnItem);
            } else {
              tempColumnArray[column.name] = columnItem;
            }
          }
        });
      });

      for (const [key, value] of Object.entries(tempColumnArray)) {
        const column = value as dynamicObj;
        if ((column.hasOwnProperty('is_extra_column') && column.is_extra_column === true) || column.always_visible || column.is_required) {
          this.tableColumns.push(column);
        }
      }
    },

    normalTableColumns() {
      const row = this.doc_store.record.tables[0].rows[0];

      if (!row) {
        return;
      }
      const tempColumnArray: any = {};
      const tempLineItemLayout = JSON.parse(
        JSON.stringify(this.lineItemLayout),
      );

      Array.from(row.columns).forEach((column: any) => {
        const allowed = this.isColumnHidden(row, column);
        if (allowed && column.name != 'id') {
          // Add only those column that are in the layout
          let label = column.name;
          let dataType = '';

          if (this.lineItemLayout.hasOwnProperty(column.name)) {
            label = this.lineItemLayout[column.name].lbl_to_display;
            dataType = this.lineItemLayout[column.name].column_type;
            delete tempLineItemLayout[column.name];
          }

          let org_index = '';
          if (column.org_index != null && column.org_index != undefined) {
            org_index = String(column.org_index);
          }

          const columnItem = {
            name: column.name,
            field: column.name,
            align: 'left',
            label: label,
            is_mapped: column.is_mapped ? column.is_mapped : false,
            org_index: org_index,
            col_origin: 'auto',
            dataType: dataType ? dataType.toLowerCase() : 'string',
            sortable: true,
          };

          columnItem.col_origin = docTablesUtil.methods.getColumnOrigin(
            this.doc_store.record.tables[0],
            column.name,
          );

          if (column.hasOwnProperty('org_index') && column.org_index != null) {
            this.tableColumns.push(columnItem);
          } else {
            tempColumnArray[column.name] = columnItem;
          }
        }
      });

      for (const [key, value] of Object.entries(tempColumnArray)) {
        this.tableColumns.push(Object.assign({}, value));
      }

      // at the end add extra column in the table that are in the layout
      for (const index in tempLineItemLayout) {
        const column = tempLineItemLayout[index];

        const allowed = !this.isCustomColumnHidden(column.name);
        if (!allowed) {
          delete tempLineItemLayout[column.name];
          continue;
        }

        const dataType = this.lineItemLayout[column.column_name].column_type;
        const columnItem = {
          name: column.column_name,
          field: column.column_name,
          align: 'left',
          label: column.lbl_to_display,
          is_mapped: column.is_mapped ? column.is_mapped : false,
          org_index: '',
          is_extra_column: true,
          col_origin: 'auto',
          dataType: dataType ? dataType.toLowerCase() : 'string',
          sortable: true,
        };

        columnItem.col_origin = docTablesUtil.methods.getColumnOrigin(
          this.doc_store.record.tables[0],
          column.column_name,
        );
        this.tableColumns.push(Object.assign({}, columnItem));
        this.layoutExtraColumns.push(column.column_name);
      }
    },

    is_AI_isColumnHidden(row: any, column: any) {
      let original_name = '';
      if (column.original_name != null && column.original_name != undefined) {
        original_name = String(column.original_name);
      }

      let allowed = true;
      if (original_name !== '') {
        allowed = this.is_AI_ColumnVisible(column);
      }

      if (original_name === '') {
        allowed = !this.isCustomColumnHidden(column.name);
      }
      return allowed;
    },

    is_AI_ColumnVisible(column: any) {
      let visbile = true;

      const original_name = this.getOrignalColumnName(column);
      if (
        !original_name ||
        !this.formatting_options ||
        !this.formatting_options.hasOwnProperty('columns_to_hide') ||
        !this.formatting_options.columns_to_hide ||
        this.formatting_options.columns_to_hide.length == 0
      ) {
        return visbile;
      }

      if (
        this.formatting_options.columns_to_hide.includes(original_name) ||
        column.name == 'UUID'
      ) {
        visbile = false;
      }

      return visbile;
    },

    isCustomColumnHidden(column_name: string) {
      let hidden = false;
      if (
        !this.custom_columns_to_hide ||
        this.custom_columns_to_hide.length == 0
      ) {
        return hidden;
      }

      for (const column of this.custom_columns_to_hide) {
        if (column === column_name) {
          hidden = true;
          break;
        }
      }

      return hidden;
    },

    getOrignalColumnName(column: any) {
      if (
        !column.hasOwnProperty('original_name') ||
        isUndefinedOrNull(column.original_name)
      ) {
        return '';
      }
      return String(column.original_name);
    },

    setFormatOptions() {
      this.formatting_options = {};
      if (
        this.doc_store.record.tables[0] &&
        this.doc_store.record.tables[0].hasOwnProperty('formatting_options')
      ) {
        this.formatting_options =
          this.doc_store.record.tables[0].formatting_options;
      }

      this.doc_store.record.tables[0].formatting_options =
        this.formatting_options;

      if (
        this.formatting_options.hasOwnProperty('columns_to_hide') &&
        this.formatting_options.columns_to_hide !== null
      ) {
        this.columns_to_hide = Object.assign(
          [],
          this.formatting_options.columns_to_hide,
        );
      }
      if (
        this.formatting_options.hasOwnProperty('custom_columns_to_hide') &&
        this.formatting_options.custom_columns_to_hide !== null
      ) {
        this.custom_columns_to_hide = Object.assign(
          [],
          this.formatting_options.custom_columns_to_hide,
        );
      }

      if (
        !this.formatting_options.hasOwnProperty('column_origins') ||
        isUndefinedOrNull(this.formatting_options.column_origins) ||
        !isObject(this.formatting_options.column_origins)
      ) {
        this.doc_store.record.tables[0].formatting_options.column_origins = {};
        this.formatting_options.column_origins = {};
      }
    },

    isColumnHidden(row: any, column: any) {
      let org_index = '';
      if (column.org_index != null && column.org_index != undefined) {
        org_index = String(column.org_index);
      }

      let allowed = true;
      if (org_index !== '') {
        allowed = this.isColumnVisible(org_index, column);
      }

      if (org_index === '') {
        allowed = !this.isCustomColumnHidden(column.name);
      }
      return allowed;
    },

    isColumnVisible(index: string, column: any) {
      if (this.doc_store.has_ai_table) {
        return this.is_AI_ColumnVisible(column);
      }

      let visbile = true;
      if (!index || !this.columns_to_hide || this.columns_to_hide.length == 0) {
        return visbile;
      }

      const formattedColsToHide = this.formatting_options?.columns_to_hide;
      if ((formattedColsToHide && formattedColsToHide.includes(index)) || column.name == 'UUID') {
        visbile = false;
      }

      return visbile;
    },

    isColumnNameValid(header: any) {
      let isValid = false;

      for (const column of this.dropdownColumns) {
        if (
          header.name === column.techName ||
          header.name == 'id' ||
          header.name == 'action_column'
        ) {
          isValid = true;
          break;
        }
      }

      return isValid;
    },

    isLineColumnAvailable(lineItem: any, column_name: string) {
      let is_column_in_table = false;
      try {
        is_column_in_table = this.isColumnInTableColumns(column_name);
      } catch (error) {
        logError(error);
      }

      if (!is_column_in_table) {
        return false;
      }

      if (lineItem.hasOwnProperty(column_name) && lineItem[column_name]) {
        return true;
      }
      return false;
    },

    isColumnInTableColumns(column_name: string) {
      let is_column_in_table = false;
      try {
        if (this.tableColumns.length == 0) {
          return is_column_in_table;
        }

        for (const column of this.tableColumns) {
          if (column.name.toLowerCase() === column_name.toLowerCase()) {
            is_column_in_table = true;
          }
        }
      } catch (error) {
        logError(error);
      }
      return is_column_in_table;
    },
  },
});
